import { useRef } from "react";
import { Link } from "react-router-dom";
import { lives } from "@/mocks/lives";
import { channels } from "@/mocks/channels";
import SectionHeading from "@/components/base/SectionHeading";
import { formatViewers } from "@/utils/format";

const latest = lives.filter((live) => live.status === "online").slice(0, 8);

export default function LatestLives() {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (direction: number) => {
    scrollerRef.current?.scrollBy({ left: direction * 320, behavior: "smooth" });
  };

  return (
    <section className="w-full px-4 md:px-8 py-10 md:py-14">
      <SectionHeading
        eyebrow="最新上线"
        icon="ri-flashlight-line"
        title="刚开播，趁热来看"
        subtitle="最新开播的直播第一时间呈现，滚动查看正在进行的精彩内容。"
      >
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            aria-label="向左滚动"
            onClick={() => scrollBy(-1)}
            className="w-9 h-9 rounded-md border border-background-300/60 flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="向右滚动"
            onClick={() => scrollBy(1)}
            className="w-9 h-9 rounded-md border border-background-300/60 flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </SectionHeading>

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1"
      >
        {latest.map((live) => {
          const channel = channels.find((item) => item.key === live.channel);
          return (
            <Link
              key={live.id}
              to={`/live/${live.id}`}
              className="group w-[260px] md:w-[300px] shrink-0 snap-start rounded-lg border border-background-200/70 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
              data-product-shop
            >
              <div className="relative w-full aspect-video">
                <img
                  src={live.cover}
                  alt={`${live.title} - ${live.streamer}`}
                  title={`${live.title} - ${live.streamer}`}
                  className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-label font-semibold text-background-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-live-pulse" />
                  直播中
                </span>
                <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-0.5 text-[11px] font-label text-background-50">
                  <i className="ri-eye-line text-xs" />
                  {formatViewers(live.viewers)}
                </span>
              </div>
              <div className="p-3.5">
                <h3 className="text-sm font-heading font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {live.title}
                </h3>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-foreground-600 truncate">{live.streamer}</span>
                  {channel && (
                    <span className="text-[11px] font-label text-secondary-700 shrink-0">
                      {channel.name}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}